import { MantineProvider, MantineThemeOverride, useMantineColorScheme } from '@mantine/core';
import { ReactNode, useEffect, useMemo } from 'react';
import { usePreferencesStore } from '../../../stores';
import { appTheme, colorSchemes, getGradientStyle, semanticColors } from '../../../theme/theme';

export interface ThemeProviderProps {
  children: ReactNode;
  theme?: MantineThemeOverride;
}

export function ThemeProvider({ children, theme = appTheme }: ThemeProviderProps) {
  const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
  const colorSchemeMode = usePreferencesStore((state) => state.colorSchemeMode);
  const fontSize = usePreferencesStore((state) => state.fontSize);
  const uiSize = usePreferencesStore((state) => state.uiSize);
  const strongFocus = usePreferencesStore((state) => state.strongFocus);
  const enableTooltips = usePreferencesStore((state) => state.enableTooltips);
  const highContrast = usePreferencesStore((state) => state.highContrast);

  // Get the current color scheme config
  const currentScheme = colorSchemes.find((s) => s.key === colorSchemeKey) || colorSchemes[0];

  const derivedSize = useMemo(() => {
    switch (uiSize) {
      case 'compact':
        return 'xs';
      case 'comfortable':
        return 'md';
      case 'spacious':
        return 'lg';
      case 'regular':
      default:
        return 'sm';
    }
  }, [uiSize]);

  // WCAG compliant base font sizes
  const derivedFontSize = useMemo(() => {
    switch (fontSize) {
      case 'xs':
        return 14; // WCAG minimum for captions
      case 'sm':
        return 15;
      case 'lg':
        return 18;
      case 'xl':
        return 20;
      case 'regular':
      default:
        return 16; // WCAG minimum for body text
    }
  }, [fontSize]);

  // Merge theme with current color scheme gradients and preference-driven defaults
  const mergedTheme: MantineThemeOverride = {
    ...theme,
    // Set primaryColor from the current color scheme
    primaryColor: currentScheme.primaryColor,
    fontSizes: {
      ...theme.fontSizes,
      xs: `${derivedFontSize - 2}px`,
      sm: `${derivedFontSize - 1}px`,
      md: `${derivedFontSize}px`,
      lg: `${derivedFontSize + 1}px`,
      xl: `${derivedFontSize + 2}px`,
    },
    components: {
      ...(theme.components || {}),
      Button: {
        ...(theme.components?.Button || {}),
        defaultProps: {
          size: derivedSize,
          ...(theme.components?.Button as any)?.defaultProps,
        },
      },
      TextInput: {
        ...(theme.components?.TextInput || {}),
        defaultProps: {
          size: derivedSize,
          ...(theme.components?.TextInput as any)?.defaultProps,
        },
      },
      Select: {
        ...(theme.components?.Select || {}),
        defaultProps: {
          size: derivedSize,
          ...(theme.components?.Select as any)?.defaultProps,
        },
      },
      PasswordInput: {
        ...(theme.components?.PasswordInput || {}),
        defaultProps: {
          size: derivedSize,
          ...(theme.components?.PasswordInput as any)?.defaultProps,
        },
      },
      Modal: {
        ...(theme.components?.Modal || {}),
        defaultProps: {
          radius: 'md',
          ...(theme.components?.Modal as any)?.defaultProps,
        },
      },
      Drawer: {
        ...(theme.components?.Drawer || {}),
        defaultProps: {
          radius: 'md',
          ...(theme.components?.Drawer as any)?.defaultProps,
        },
      },
      Tooltip: {
        ...(theme.components?.Tooltip || {}),
        defaultProps: {
          disabled: enableTooltips === false,
          ...(theme.components?.Tooltip as any)?.defaultProps,
        },
      },
    },
    focusRing: strongFocus ? 'auto' : 'never',
    primaryShade: highContrast ? 8 : (theme.primaryShade || { light: 6, dark: 8 }),
    defaultRadius: highContrast ? 'sm' : (theme.defaultRadius || 'md'),
    other: {
      ...theme.other,
      currentScheme,
      gradients: {
        ...theme.other?.gradients,
        primary: currentScheme.primary,
        secondary: currentScheme.secondary,
        tertiary: currentScheme.tertiary,
      },
      ui: {
        size: uiSize,
        fontSize,
        strongFocus,
        enableTooltips,
        highContrast,
      },
    },
  };

  return (
    <MantineProvider theme={mergedTheme} defaultColorScheme={colorSchemeMode === 'auto' ? 'auto' : colorSchemeMode}>
      <ThemeInjector />
      {children}
    </MantineProvider>
  );
}

// Injects CSS custom properties for current theme and handles dynamic color scheme changes
function ThemeInjector() {
  const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
  const colorSchemeMode = usePreferencesStore((state) => state.colorSchemeMode);
  const highContrast = usePreferencesStore((state) => state.highContrast);
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  // Handle dynamic color scheme mode changes (light/dark/auto)
  useEffect(() => {
    if (colorSchemeMode === 'auto') {
      // Detect system preference and apply it
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const applySystemPreference = () => {
        setColorScheme(mediaQuery.matches ? 'dark' : 'light');
      };

      // Apply immediately
      applySystemPreference();

      // Listen for system preference changes
      mediaQuery.addEventListener('change', applySystemPreference);
      return () => mediaQuery.removeEventListener('change', applySystemPreference);
    } else {
      setColorScheme(colorSchemeMode);
    }
  }, [colorSchemeMode, setColorScheme]);

  useEffect(() => {
    const currentScheme = colorSchemes.find((s) => s.key === colorSchemeKey) || colorSchemes[0];
    const root = document.documentElement;

    // Set gradient CSS variables
    root.style.setProperty('--gradient-primary', getGradientStyle(currentScheme.primary));
    root.style.setProperty('--gradient-secondary', getGradientStyle(currentScheme.secondary));
    root.style.setProperty('--gradient-tertiary', getGradientStyle(currentScheme.tertiary));

    // Set color variables
    root.style.setProperty('--color-primary-from', currentScheme.primary.from);
    root.style.setProperty('--color-primary-to', currentScheme.primary.to);
    root.style.setProperty('--color-secondary-from', currentScheme.secondary.from);
    root.style.setProperty('--color-secondary-to', currentScheme.secondary.to);
    root.style.setProperty('--color-tertiary-from', currentScheme.tertiary.from);
    root.style.setProperty('--color-tertiary-to', currentScheme.tertiary.to);

    // Set semantic color variables based on current color scheme
    const isDark = colorScheme === 'dark';
    const colors = isDark ? semanticColors.dark : semanticColors.light;

    root.style.setProperty('--semantic-text-primary', colors.text.primary);
    root.style.setProperty('--semantic-text-secondary', colors.text.secondary);
    root.style.setProperty('--semantic-text-muted', colors.text.muted);
    root.style.setProperty('--semantic-text-most-muted', colors.text.mostMuted);
    root.style.setProperty('--semantic-surface-base', colors.surface.base);
    root.style.setProperty('--semantic-surface-raised', colors.surface.raised);
    root.style.setProperty('--semantic-border-subtle', colors.border.subtle);
    root.style.setProperty('--semantic-border-strong', colors.border.strong);

    // Set theme mode
    root.setAttribute('data-theme', colorScheme);
    root.setAttribute('data-color-scheme', colorSchemeKey);
    root.toggleAttribute('data-high-contrast', Boolean(highContrast));
  }, [colorSchemeKey, colorScheme, highContrast]);

  return null;
}

export default ThemeProvider;
